<template>
  <ElDialog
    :visible="value"
    :title="$t('ADVERTISEMENT.SEARCH_TERMS.ADD_KEYWORD_TITLE')"
    width="40rem"
    append-to-body
    class="add-keyword-from-search-terms-modal"
    @open="createModel"
    @close="closeAndReset"
  >
    <div class="row">
      <div class="columns">
        <ElAlert
          v-if="showAlert"
          class="add-keyword-from-search-terms-modal__alert"
          :closable="false"
          type="success"
          effect="dark"
          :title="addedKeywordMessage"
          show-icon
        />
      </div>
    </div>
    <ElForm
      ref="keywordForm"
      :model="keywordModelList"
      class="add-keyword-from-search-terms-modal__keyword-form"
    >
      <div class="row add-keyword-from-search-terms-modal__form-heading">
        <div class="columns">
          <div class="row">
            <div class="columns small-8">
              <div class="row align-justify">
                <div class="columns shrink">
                  {{ $t('ADVERTISEMENT.BID.KEYWORD') }}
                </div>
                <div class="columns shrink">
                  <MatchingTypeHelp />
                </div>
              </div>
            </div>
            <div class="columns small-4">
              {{ $t('ADVERTISEMENT.BID.MAX_CPI') }}
            </div>
          </div>
          <hr
            class="row is-collapse-child add-keyword-from-search-terms-modal__form-heading-hr"
          />
        </div>
      </div>
      <SearchTermKeywordInput
        v-for="(keywordModel, index) in keywordModelList.items"
        :key="keywordModel.id"
        :error="getError(keywordModel.query)"
        :matching-type.sync="keywordModel.matchingType"
        :query.sync="keywordModel.query"
        :price-offer.sync="keywordModel.priceOffer"
        :query-prop="'items.' + index + '.query'"
        :price-offer-prop="'items.' + index + '.priceOffer'"
        class="add-keyword-from-search-terms-modal__keyword-input"
      />
    </ElForm>
    <div slot="footer">
      <ElButton
        type="primary"
        native-type="submit"
        size="large"
        :loading="isLoading"
        @click="addBids"
      >
        {{ $t('ADVERTISEMENT.ADD_BID_BTN') }}
      </ElButton>
    </div>
  </ElDialog>
</template>

<script>
import form from '@/mixins/form';
import campaignId from '@/mixins/advertisement/campaignId';

import SearchTermKeywordInput from './SearchTermKeywordInput';

import MatchingTypeHelp from '../../../common/MatchingTypeHelp';

import { AD_CAMPAIGN_SEARCH_TERM_ADD_BID_MODAL_ACTION_BUTTON } from '@/constants/gtm-events';

import {
  convertArrayOfObjectToHashTable,
  convertArrayOfPrimitiveToHashTable,
} from '@/utils/arrayToHashTable';

import { SearchTerms } from '@/api/api';

export default {
  name: 'AddKeywordFromSearchTermsModal',

  components: {
    SearchTermKeywordInput,
    MatchingTypeHelp,
  },

  mixins: [form, campaignId],

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    selections: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      keywordModelList: {
        items: [],
      },
      addedKeywords: [],
      errors: {},
    };
  },
  computed: {
    addedKeywordMessage() {
      return this.addedKeywords.length > 1
        ? this.$tc('ADVERTISEMENT.SEARCH_TERMS.ADD_BIDS_SUCCESS', 1, {
            keywords: this.addedKeywords.join(' / '),
          })
        : this.$tc('ADVERTISEMENT.SEARCH_TERMS.ADD_BIDS_SUCCESS', 0, {
            keyword: this.addedKeywords.join(' / '),
          });
    },
    showAlert() {
      return Object.keys(this.errors).length && this.addedKeywords.length;
    },
  },
  methods: {
    getError(keyword) {
      return this.errors[keyword] ? this.errors[keyword].message : '';
    },
    createModel() {
      this.keywordModelList = {
        items: this.selections.map(({ id, query, matchingType }) => ({
          id,
          matchingType,
          query,
          priceOffer: 0,
        })),
      };
    },
    closeAndReset() {
      this.resetForm('keywordForm');

      this.errors = {};
      this.addedKeywords = [];

      this.$emit('input', false);
    },
    addBids() {
      if (!this.isValid('keywordForm')) return;
      this.isLoading = true;

      this.$gtm.trackEvent(AD_CAMPAIGN_SEARCH_TERM_ADD_BID_MODAL_ACTION_BUTTON);

      return SearchTerms.addBulkBids({
        campaignId: this.campaignId,
        bids: this.keywordModelList.items,
      })
        .then(({ data, errors, message }) => {
          this.errors = convertArrayOfObjectToHashTable(
            errors,
            ({ object: { query } }) => query,
          );

          this.addedKeywords = data.map(({ query }) => query);

          const addedKeywordsHashMap = convertArrayOfPrimitiveToHashTable(
            this.addedKeywords,
          );

          this.keywordModelList.items = this.keywordModelList.items.filter(
            ({ query }) => !(query in addedKeywordsHashMap),
          );

          if (!errors.length) {
            this.$message.success(message);
            this.closeAndReset();
          }
        })
        .catch(({ message }) => {
          if (message) this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.add-keyword-from-search-terms-modal {
  &__alert {
    margin-bottom: $global-margin * 2;
  }

  &__keyword-input {
    & + & {
      margin-top: $global-margin;
    }

    .el-form-item {
      margin-bottom: 0;
    }
  }

  &__form-heading {
    &-hr {
      margin-top: $global-margin / 2;
    }
  }
}
</style>
